import { Layout } from "providers/Layout";
import { useCallback, useMemo, useState } from "react";
import { type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { useAuthContext } from "providers/Auth";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Badge,
  BadgeSize,
  BadgeVariant,
  Button,
  ButtonSize,
  ButtonVariant,
  Show
} from "shared/components";
import { BreadCrumbs } from "shared/components/BreadCrumbs";
import { Card } from "shared/components/Card";
import { Plus } from "untitledui-js-base";
import { AddIntegrationModal } from "views/settings-integration/components/AddIntegrationModal";
import { ImportLockboxesModal } from "views/settings-integration/integrations/shared/ImportLockboxesModal";
import { codeboxApi } from "api/codebox";
import { rentlyApi } from "api/rently";
import { useSettingsController } from "views/settings-integration/controller";
import { PMSNames } from "./integrations/pms/constants";
import { IntegrationLogoIcon } from "views/settings-integration/components/IntegrationLogoIcon";
import { IntegrationId } from "views/settings-integration/components/AddIntegrationModal/types";
import { PropertyManagementSystem } from "shared/types/partner";
import {
  getIntegrationCredentials,
  QUERY_INTEGRATION_CREDENTIALS
} from "api/landlord/integrations";
import { metaAdsApi } from "api/meta-ads";
import { QUERY_META_ADS_STATUS } from "views/settings-integration/integrations/meta-ads/constants";
import NoIntegrationsIcon from "shared/icons/noIntegrations.svg?react";
import { AhLeadUsageTracker } from "views/settings-integration/integrations/affordable-housing-com/AhLeadUsageTracker";
import { useAhLeadUsage } from "views/settings-integration/integrations/affordable-housing-com/useAhLeadUsage";
import { cx } from "shared/utils";

const OTHER_INTEGRATION_IDS: Record<
  string,
  PropertyManagementSystem | IntegrationId
> = {
  HubSpot: IntegrationId.HUBSPOT,
  "Google Analytics": IntegrationId.GA,
  "Google Ads": IntegrationId.GOOGLE_ADS,
  "Meta Lead Ads": IntegrationId.META_ADS,
  BoomFinance: IntegrationId.BOOM_FINANCE,
  "AffordableHousing.com": IntegrationId.AFFORDABLE_HOUSING_COM,
  "Property Shield": IntegrationId.PROPERTY_SHIELD
};

interface IntegrationsItem {
  enabled: boolean;
  title: string;
  description: string;
  link: string;
  icon?: ReactNode;
}

const isCrmFeedConnected = (isBoomCRM: boolean, connected?: boolean) =>
  isBoomCRM && Boolean(connected);

export default function IntegrationsView() {
  const [showAddIntegrationModal, setShowAddIntegrationModal] = useState(false);
  const [importLockboxesOpen, setImportLockboxesOpen] = useState(false);
  const [importRentlyLockboxesOpen, setImportRentlyLockboxesOpen] =
    useState(false);
  const queryClient = useQueryClient();

  const handleImportSuccess = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ["LOCKBOXES"] });
    setImportLockboxesOpen(false);
  }, [queryClient]);

  const handleRentlyImportSuccess = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ["LOCKBOXES"] });
    setImportRentlyLockboxesOpen(false);
  }, [queryClient]);
  const navigate = useNavigate();
  const { partner, type } = useAuthContext();
  const { propertyManagementSystem, currentSettings, hubspotHubId } =
    useSettingsController();

  const { data: credentialsData } = useQuery({
    queryKey: [QUERY_INTEGRATION_CREDENTIALS],
    queryFn: getIntegrationCredentials,
    enabled: type.isMultiPMS
  });

  const { data: metaAdsStatus } = useQuery({
    queryKey: QUERY_META_ADS_STATUS,
    queryFn: metaAdsApi.getStatus,
    enabled: type.isBoomCRM
  });

  const isPropertyShieldConnected = type.isListingFraudAlerts;

  const integrationCredentials = credentialsData?.integration_credentials ?? [];

  const isAhConnected =
    type.isBoomCRM && Boolean(partner?.affordable_housing_com_connected);
  const ahLeadUsage = useAhLeadUsage({ enabled: isAhConnected });
  const ahCardLimitClass = ahLeadUsage?.isLimitReached
    ? "!border-danger-200"
    : "";

  const integrations: IntegrationsItem[] = useMemo(() => {
    const pmsTitle = propertyManagementSystem
      ? PMSNames[propertyManagementSystem]
      : "PMS"; //this is fallback which should not happen, added just to be safe

    const connectedIntegrations: {
      connected: boolean;
      title: string;
      link: string;
      integrationId: IntegrationId;
    }[] = [
      {
        connected: type.isBoomCRM && Boolean(partner?.codebox_connected),
        title: "Codebox",
        link: "/settings/integration/codebox",
        integrationId: IntegrationId.CODEBOX
      },
      {
        connected: type.isBoomCRM && Boolean(partner?.sentrilock_connected),
        title: "SentriLock",
        link: "/settings/integration/sentrilock",
        integrationId: IntegrationId.SENTRILOCK
      },
      {
        connected: type.isBoomCRM && Boolean(partner?.igloohome_connected),
        title: "Igloohome",
        link: "/settings/integration/igloohome",
        integrationId: IntegrationId.IGLOOHOME
      },
      {
        connected:
          type.isBoomCRM && Boolean(partner?.rently_lockboxes_connected),
        title: "Rently Lockboxes",
        link: "/settings/integration/rently-lockboxes",
        integrationId: IntegrationId.RENTLY_LOCKBOXES
      },
      {
        connected: type.isBoomCRM && Boolean(partner?.zillow_connected),
        title: "Zillow",
        link: "/settings/integration/zillow",
        integrationId: IntegrationId.ZILLOW
      },
      {
        connected: type.isBoomCRM && Boolean(partner?.zumper_connected),
        title: "Zumper",
        link: "/settings/integration/zumper",
        integrationId: IntegrationId.ZUMPER
      },
      {
        connected: type.isBoomCRM && Boolean(partner?.dwellsy_connected),
        title: "Dwellsy",
        link: "/settings/integration/dwellsy",
        integrationId: IntegrationId.DWELLSY
      },
      {
        connected: type.isBoomCRM && Boolean(partner?.apartments_com_connected),
        title: "Apartments.com",
        link: "/settings/integration/apartments-com",
        integrationId: IntegrationId.APARTMENTS_COM
      },
      {
        connected: type.isBoomCRM && Boolean(partner?.mhvillage_connected),
        title: "MHVillage",
        link: "/settings/integration/mhvillage",
        integrationId: IntegrationId.MHVILLAGE
      },
      {
        connected:
          type.isBoomCRM && Boolean(partner?.affordable_housing_com_connected),
        title: "AffordableHousing.com",
        link: "/settings/integration/affordable-housing-com",
        integrationId: IntegrationId.AFFORDABLE_HOUSING_COM
      },
      {
        connected: type.isBoomCRM && Boolean(metaAdsStatus?.connected),
        title: "Meta Lead Ads",
        link: "/settings/integration/meta-ads",
        integrationId: IntegrationId.META_ADS
      },
      {
        connected: Boolean(partner?.boom_finance_external_id),
        title: "BoomFinance",
        link: "/settings/integration/boom-finance",
        integrationId: IntegrationId.BOOM_FINANCE
      },
      {
        connected: type.isBoomCRM && Boolean(partner?.rental_beast_connected),
        title: "Rental Beast",
        link: "/settings/integration/rental-beast",
        integrationId: IntegrationId.RENTAL_BEAST
      },
      {
        connected: isCrmFeedConnected(type.isBoomCRM, partner?.uloop_connected),
        title: "Uloop",
        link: "/settings/integration/uloop",
        integrationId: IntegrationId.ULOOP
      },
      {
        connected: isCrmFeedConnected(
          type.isBoomCRM,
          partner?.rental_source_connected
        ),
        title: "Rental Source",
        link: "/settings/integration/rental-source",
        integrationId: IntegrationId.RENTAL_SOURCE
      },
      {
        connected: isPropertyShieldConnected,
        title: "Property Shield",
        link: "/settings/integration/property-shield",
        integrationId: IntegrationId.PROPERTY_SHIELD
      }
    ];

    return [
      {
        enabled: Boolean(hubspotHubId),
        title: "HubSpot",
        description: "Integration type: HubSpot",
        link: "/settings/integration/hubspot",
        icon: <IntegrationLogoIcon id={IntegrationId.HUBSPOT} size="BASE" />
      },
      {
        enabled: Boolean(currentSettings.google.googleAnalyticsId),
        title: "Google Analytics",
        description: "Integration type: Google Analytics",
        link: "/settings/integration/google-analytics",
        icon: <IntegrationLogoIcon id={IntegrationId.GA} size="BASE" />
      },
      {
        enabled: Boolean(currentSettings.googleAds.conversionId),
        title: "Google Ads",
        description: "Integration type: Google Ads",
        link: "/settings/integration/google-ads",
        icon: <IntegrationLogoIcon id={IntegrationId.GOOGLE_ADS} size="BASE" />
      },
      {
        enabled: Boolean(propertyManagementSystem),
        title: pmsTitle,
        description: `Integration type: ${pmsTitle}`,
        link: "/settings/integration/pms",
        icon: propertyManagementSystem ? (
          <IntegrationLogoIcon id={propertyManagementSystem} size="BASE" />
        ) : undefined
      },
      ...connectedIntegrations
        .filter((integration) => integration.connected)
        .map((integration) => ({
          enabled: true,
          title: integration.title,
          description: `Integration type: ${integration.title}`,
          link: integration.link,
          icon: (
            <IntegrationLogoIcon id={integration.integrationId} size="BASE" />
          )
        }))
    ].sort((a, b) => a.title.localeCompare(b.title));
  }, [
    propertyManagementSystem,
    currentSettings,
    partner,
    type.isBoomCRM,
    isPropertyShieldConnected,
    hubspotHubId,
    metaAdsStatus?.connected
  ]);

  const pageHeader = (
    <>
      <div className="flex items-center justify-between border-b border-alpha-40 px-6 pb-[22px]">
        <BreadCrumbs
          crumbs={[
            {
              title: "Settings",
              href: "/settings"
            },
            {
              title: "Integration",
              href: "/settings/integration"
            }
          ]}
        />
      </div>
      <div className="flex items-center justify-between p-6 pb-3">
        <h1 className="text-xl font-bold text-content-1000">Integration</h1>
        <Button
          variant={ButtonVariant.PRIMARY}
          size={ButtonSize.SM}
          Icon={Plus}
          onClick={() => setShowAddIntegrationModal(true)}
        >
          Add integration
        </Button>
      </div>
    </>
  );

  type IntegrationTile = {
    type: "credential" | "integration";
    id: number | string;
    title: string;
    description: string;
    integrationId?: PropertyManagementSystem | IntegrationId;
    icon?: ReactNode;
    link: string;
  };

  const allIntegrationTiles = useMemo(() => {
    const tiles: IntegrationTile[] = [];

    // Add PMS credential tiles
    integrationCredentials.forEach((credential) => {
      const pmsLookup: Partial<Record<string, string>> = PMSNames;
      const pmsName = credential.integration
        ? (pmsLookup[credential.integration] ?? credential.integration)
        : "PMS";

      const integrationId:
        | PropertyManagementSystem
        | IntegrationId
        | undefined = credential.integration ?? undefined;

      tiles.push({
        type: "credential" as const,
        id: credential.id,
        title: credential.name,
        description: `Integration type: ${pmsName}`,
        integrationId,
        link: `/settings/integration/pms/${credential.id}`
      });
    });

    // Add other integration tiles (skip legacy PMS card)
    integrations.forEach((integration) => {
      if (
        integration.enabled &&
        integration.link !== "/settings/integration/pms"
      ) {
        const integrationId = OTHER_INTEGRATION_IDS[integration.title];
        tiles.push({
          type: "integration" as const,
          id: integration.title,
          title: integration.title,
          description: integration.description,
          icon: integration.icon,
          integrationId,
          link: integration.link
        });
      }
    });

    return tiles.sort((a, b) => a.title.localeCompare(b.title));
  }, [integrationCredentials, integrations]);

  if (type.isMultiPMS) {
    const hasAnyIntegrations = allIntegrationTiles.length > 0;

    return (
      <Layout
        headerClassName="border-b print:border-none border-alpha-100 bg-white pt-[22px]"
        header={pageHeader}
      >
        {!hasAnyIntegrations ? (
          <div className="flex items-center justify-center py-20">
            <div className="flex flex-col items-center justify-center gap-6 w-[424px]">
              {/* Illustration with circular background */}
              <div className="relative flex items-center justify-center size-[100px] rounded-[115px] bg-[rgba(28,29,34,0.06)]">
                <NoIntegrationsIcon className="text-[#1C1D22]" />
              </div>

              {/* Text content */}
              <div className="flex flex-col gap-2 items-center justify-center text-center">
                <p className="text-base font-bold text-content-700">
                  There&apos;s no integration yet
                </p>
                <p className="text-sm text-content-400 max-w-[360px]">
                  Add feature-rich two-way integration with you property
                  management software (PMS)
                </p>
              </div>

              {/* Button */}
              <Button
                variant={ButtonVariant.PRIMARY}
                size={ButtonSize.BASE}
                Icon={Plus}
                onClick={() => setShowAddIntegrationModal(true)}
              >
                Add integration
              </Button>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-3 gap-4">
            {allIntegrationTiles.map((tile) => (
              <Card
                className={cx(
                  "flex flex-col p-8",
                  tile.integrationId === IntegrationId.AFFORDABLE_HOUSING_COM
                    ? ahCardLimitClass
                    : ""
                )}
                key={tile.id}
              >
                <div className="flex items-start justify-between">
                  {tile.integrationId ? (
                    <IntegrationLogoIcon id={tile.integrationId} size="BASE" />
                  ) : (
                    (tile.icon ?? <span />)
                  )}
                  <Badge variant={BadgeVariant.SUCCESS} size={BadgeSize.SM}>
                    Connected
                  </Badge>
                </div>

                <div className="mt-6 mb-4 h-full">
                  <div className="space-y-2">
                    <p className="font-bold text-base text-content-1000">
                      {tile.title}
                    </p>
                    <p className="text-sm text-content-400">
                      {tile.description}
                    </p>
                  </div>
                  {tile.integrationId ===
                    IntegrationId.AFFORDABLE_HOUSING_COM && ahLeadUsage ? (
                    <AhLeadUsageTracker usage={ahLeadUsage} />
                  ) : null}
                </div>

                <div className="flex border-t border-alpha-100 pt-4 items-end">
                  <Button
                    variant={ButtonVariant.TEXT}
                    size={ButtonSize.BASE}
                    onClick={() => navigate(tile.link)}
                  >
                    Configure
                  </Button>
                </div>
              </Card>
            ))}
          </div>
        )}

        <Show if={showAddIntegrationModal}>
          <AddIntegrationModal
            opened={showAddIntegrationModal}
            onClose={() => setShowAddIntegrationModal(false)}
            onConnectSuccess={(id) => {
              if (id === IntegrationId.CODEBOX) setImportLockboxesOpen(true);
              if (id === IntegrationId.RENTLY_LOCKBOXES)
                setImportRentlyLockboxesOpen(true);
            }}
          />
        </Show>

        <ImportLockboxesModal
          open={importLockboxesOpen}
          onClose={() => setImportLockboxesOpen(false)}
          onSuccess={handleImportSuccess}
          vendor="codebox"
          emptyStateDescription="There are no lockboxes in your Codebox account."
          noAccountsDescription="No Codebox accounts connected. Add an account before importing lockboxes."
          api={codeboxApi}
        />

        <ImportLockboxesModal
          open={importRentlyLockboxesOpen}
          onClose={() => setImportRentlyLockboxesOpen(false)}
          onSuccess={handleRentlyImportSuccess}
          vendor="rently"
          emptyStateDescription="There are no lockboxes on this Rently account that we can import."
          noAccountsDescription="No Rently accounts connected. Connect an account before importing lockboxes."
          api={rentlyApi}
        />
      </Layout>
    );
  }

  // Legacy flow for non-multi-PMS partners
  return (
    <Layout
      headerClassName="border-b print:border-none border-alpha-100 bg-white pt-[22px]"
      header={pageHeader}
    >
      <div className="flex flex-wrap gap-4">
        {integrations.map((integration) => {
          if (!integration.enabled) return null;

          return (
            <Card
              className={cx(
                "flex flex-col p-8 w-[368px]",
                integration.title === "AffordableHousing.com"
                  ? ahCardLimitClass
                  : ""
              )}
              key={integration.title}
            >
              <div className="flex items-start justify-between">
                {integration.icon ?? <span />}
                <Badge variant={BadgeVariant.SUCCESS} size={BadgeSize.SM}>
                  Connected
                </Badge>
              </div>

              <div className="mt-6 mb-4 h-full">
                <div className="space-y-2">
                  <p className="font-bold text-base text-content-1000">
                    {integration.title}
                  </p>
                  <p className="text-sm text-content-400">
                    {integration.description}
                  </p>
                </div>
                {integration.title === "AffordableHousing.com" &&
                ahLeadUsage ? (
                  <AhLeadUsageTracker usage={ahLeadUsage} />
                ) : null}
              </div>

              <div className="flex border-t border-alpha-100 pt-4 items-end">
                <Button
                  variant={ButtonVariant.TEXT}
                  size={ButtonSize.BASE}
                  onClick={() => navigate(integration.link)}
                >
                  Configure
                </Button>
              </div>
            </Card>
          );
        })}
      </div>

      {/* We need this show to drop all states in modal when it closes */}
      <Show if={showAddIntegrationModal}>
        <AddIntegrationModal
          opened={showAddIntegrationModal}
          onClose={() => setShowAddIntegrationModal(false)}
          onConnectSuccess={(id) => {
            if (id === IntegrationId.CODEBOX) setImportLockboxesOpen(true);
            if (id === IntegrationId.RENTLY_LOCKBOXES)
              setImportRentlyLockboxesOpen(true);
          }}
        />
      </Show>

      <ImportLockboxesModal
        open={importLockboxesOpen}
        onClose={() => setImportLockboxesOpen(false)}
        onSuccess={handleImportSuccess}
        vendor="codebox"
        emptyStateDescription="There are no lockboxes in your Codebox account."
        noAccountsDescription="No Codebox accounts connected. Add an account before importing lockboxes."
        api={codeboxApi}
      />

      <ImportLockboxesModal
        open={importRentlyLockboxesOpen}
        onClose={() => setImportRentlyLockboxesOpen(false)}
        onSuccess={handleRentlyImportSuccess}
        vendor="rently"
        emptyStateDescription="There are no lockboxes on this Rently account that we can import."
        noAccountsDescription="No Rently accounts connected. Connect an account before importing lockboxes."
        api={rentlyApi}
      />
    </Layout>
  );
}
